import React, { useEffect, useState } from 'react';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumberFromString } from 'libphonenumber-js/max';
import { useAppSelector } from '../../../../common/hooks/useAppSelector';
import { useAppDispatch } from '../../../../common/hooks/useAppDispatch';
import { fetchCompanies, selectAllCompanies } from '../../../../slices/companies/companiesSlice';
import {
  fetchBusinessProfileById,
  selectBusinessProfileDetails,
  updateBusinessProfileEntry,
} from '../../../../slices/business-profile/businessProfileSlice';
import WhatsAppOptInCheckbox from '../../../../common/whatsapp-opt-in-checkbox/WhatsAppOptInCheckbox';
import { ROUTES } from '../../../../common/constants/routes';

export const NotificationsTab = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectAllCompanies)[0];
  const details = useAppSelector(selectBusinessProfileDetails);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!company?.id) {
      dispatch(fetchCompanies());
    }
  }, [dispatch, company?.id]);

  useEffect(() => {
    if (company?.id && !details?.id) {
      dispatch(fetchBusinessProfileById({ id: company.id as number }));
    }
  }, [dispatch, company?.id, details?.id]);

  const handleChange = (value: boolean) => {
    if (!company?.id || !details?.id) return;
    setSaving(true);
    dispatch(
      updateBusinessProfileEntry({
        companyId: company.id as number,
        detailsId: details.id as number,
        data: { ...details, marketing_opt_in: value },
      })
    ).finally(() => setSaving(false));
  };

  const parsed = details?.business_phone_number
    ? parsePhoneNumberFromString(String(details.business_phone_number))
    : undefined;
  const phoneType = parsed?.getType();
  const reachable =
    Boolean(parsed?.isValid()) && (phoneType === 'MOBILE' || phoneType === 'FIXED_LINE_OR_MOBILE');

  if (!details?.id) {
    return (
      <Grid component='div' container style={{ justifyContent: 'center' }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid component='div' sx={{ mb: 3 }}>
      <Typography variant='h6' sx={{ mb: 1 }}>
        {t('settings.titles.notifications')}
      </Typography>
      <WhatsAppOptInCheckbox
        value={Boolean(details.marketing_opt_in)}
        onChange={handleChange}
        disabled={saving || (!reachable && !details.marketing_opt_in)}
      />
      {!reachable && (
        <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
          {t('messages.whatsappNeedsMobile')}{' '}
          <Link className='link-brand-color' to={ROUTES.BUSINESS_PROFILE}>
            {t('links.businessProfile')}
          </Link>
        </Typography>
      )}
    </Grid>
  );
};
